import type { ClinicSettings, MessageTemplate } from '@/lib/clinic/types'

export const PATIENT_CODE_PREFIX = 'DSC'

export const PRESCRIPTION_CODE_PREFIX = 'RX'

export const DEFAULT_COMPLAINTS: string[] = [
    'Tooth pain',
    'Tooth sensitivity',
    'Gum bleeding',
    'Swelling',
    'Tooth decay',
    'Broken tooth',
    'Missing tooth',
    'Bad breath',
    'Routine dental checkup',
    'Other'
]

export const DEFAULT_TREATMENTS: string[] = [
    'Teeth Cleaning / Scaling',
    'Root Canal Treatment',
    'Tooth Extraction',
    'Dental Filling',
    'Dental Crown',
    'Dental Bridge',
    'Teeth Whitening',
    'Denture',
    'Gum Treatment',
    'Dental Checkup',
    'Other'
]

export const DEFAULT_MEDICINE_INSTRUCTIONS: string[] = [
    'After food',
    'Before food',
    'Before breakfast',
    'At bedtime',
    'Only if pain persists',
    'Rinse for 30 seconds, do not swallow',
    'Apply on the affected area',
    'Gargle and spit'
]

export const FREQUENCY_OPTIONS: string[] = [
    '1-0-1',
    '1-1-1',
    '1-0-0',
    '0-0-1',
    '0-1-0',
    'SOS',
    'Once daily',
    'Twice daily',
    'Three times daily'
]

export const DURATION_OPTIONS: string[] = [
    '1 day',
    '3 days',
    '5 days',
    '7 days',
    '10 days',
    '14 days',
    '1 month'
]

export const DOSAGE_OPTIONS: string[] = [
    '1 tablet',
    '1 capsule',
    '½ tablet',
    '5 ml',
    '10 ml',
    'Pea-sized amount',
    'Thin layer'
]

export interface MedicinePreset {
    name: string
    dosage: string
    frequency: string
    duration: string
    instructions: string
}

export const COMMON_MEDICINES: MedicinePreset[] = [
    { name: 'Amoxicillin 500 mg', dosage: '1 capsule', frequency: '1-1-1', duration: '5 days', instructions: 'After food' },
    { name: 'Aceclofenac 100 mg + Paracetamol 325 mg', dosage: '1 tablet', frequency: '1-0-1', duration: '3 days', instructions: 'After food' },
    { name: 'Metronidazole 400 mg', dosage: '1 tablet', frequency: '1-1-1', duration: '5 days', instructions: 'After food' },
    { name: 'Chlorhexidine 0.2% Mouthwash', dosage: '10 ml', frequency: '1-0-1', duration: '14 days', instructions: 'Rinse for 30 seconds, do not swallow' },
    { name: 'Pantoprazole 40 mg', dosage: '1 tablet', frequency: '1-0-0', duration: '5 days', instructions: 'Before breakfast' },
    { name: 'Ibuprofen 400 mg', dosage: '1 tablet', frequency: 'SOS', duration: '3 days', instructions: 'Only if pain persists' },
    { name: 'Amoxicillin + Clavulanic Acid 625 mg', dosage: '1 tablet', frequency: '1-0-1', duration: '5 days', instructions: 'After food' },
    { name: 'Azithromycin 500 mg', dosage: '1 tablet', frequency: '1-0-0', duration: '3 days', instructions: 'Before food' },
    { name: 'Paracetamol 650 mg', dosage: '1 tablet', frequency: '1-0-1', duration: '3 days', instructions: 'After food' },
    { name: 'Ketorolac 10 mg', dosage: '1 tablet', frequency: 'SOS', duration: '2 days', instructions: 'Only if pain persists' },
    { name: 'Potassium Nitrate 5% Toothpaste', dosage: 'Pea-sized amount', frequency: '1-0-1', duration: '1 month', instructions: 'Brush gently, do not rinse immediately' },
    { name: 'Choline Salicylate Oral Gel', dosage: 'Thin layer', frequency: '1-1-1', duration: '5 days', instructions: 'Apply on the affected area' },
    { name: 'Povidone-Iodine 2% Gargle', dosage: '10 ml', frequency: '1-1-1', duration: '5 days', instructions: 'Gargle and spit' },
    { name: 'Vitamin B-Complex', dosage: '1 capsule', frequency: '0-0-1', duration: '14 days', instructions: 'After food' }
]

export const QUICK_MEDICINE_COUNT = 6

export const DIAGNOSIS_SUGGESTIONS: string[] = [
    'Dental caries',
    'Reversible pulpitis',
    'Symptomatic irreversible pulpitis',
    'Chronic periapical abscess',
    'Chronic generalised gingivitis',
    'Chronic periodontitis',
    'Acute pericoronitis',
    'Dentinal hypersensitivity',
    'Fractured tooth',
    'Grossly decayed tooth',
    'Partially edentulous',
    'Completely edentulous',
    'Tooth discolouration',
    'Minor aphthous ulcer',
    'Impacted third molar'
]

export const MEDICAL_HISTORY_SUGGESTIONS: string[] = [
    'Diabetes',
    'Hypertension',
    'Heart disease',
    'Asthma',
    'Thyroid disorder',
    'Pregnant',
    'On blood thinners',
    'No significant history'
]

export const ALLERGY_SUGGESTIONS: string[] = [
    'Penicillin',
    'Sulfa drugs',
    'NSAIDs',
    'Latex',
    'Local anaesthetic',
    'No known allergies'
]

export interface FollowUpInterval {
    label: string
    days?: number
    months?: number
}

export const FOLLOW_UP_INTERVALS: FollowUpInterval[] = [
    { label: '3 days', days: 3 },
    { label: '1 week', days: 7 },
    { label: '2 weeks', days: 14 },
    { label: '1 month', months: 1 },
    { label: '3 months', months: 3 },
    { label: '6 months', months: 6 }
]

export const FOLLOW_UP_NOTE_SUGGESTIONS: string[] = [
    'Review healing',
    'Suture removal',
    'RCT next sitting',
    'Crown trial and cementation',
    'Recheck sensitivity',
    'Routine checkup and cleaning'
]

export interface TemplatePlaceholder {
    token: string
    description: string
}

export const TEMPLATE_PLACEHOLDERS: TemplatePlaceholder[] = [
    { token: '[Patient Name]', description: "Patient's full name" },
    { token: '[Clinic Name]', description: 'Clinic name from settings' },
    { token: '[Doctor Name]', description: 'Doctor name from settings' },
    { token: '[Follow-up Date]', description: 'Returning date of the follow-up' },
    { token: '[Treatment]', description: 'Previous treatment' },
    { token: '[Clinic Phone]', description: 'Clinic contact number' }
]

export const DEFAULT_TEMPLATES: MessageTemplate[] = [
    {
        id: 'tpl-follow-up',
        name: 'Follow-up reminder',
        body: 'Hello [Patient Name], this is a friendly follow-up reminder from [Clinic Name]. We hope you are doing well. Please contact us regarding your next dental visit.'
    },
    {
        id: 'tpl-due-today',
        name: 'Visit due today',
        body: 'Hello [Patient Name], your follow-up visit at [Clinic Name] is due today ([Follow-up Date]) for [Treatment]. Please call us on [Clinic Phone] if you need a different time.'
    },
    {
        id: 'tpl-missed',
        name: 'Missed follow-up',
        body: 'Hello [Patient Name], we noticed you could not make it for your follow-up on [Follow-up Date]. Your dental care is important to us. Please contact [Clinic Name] on [Clinic Phone] to plan your next visit.'
    },
    {
        id: 'tpl-aftercare',
        name: 'After-treatment check-in',
        body: 'Hello [Patient Name], this is [Doctor Name] from [Clinic Name]. We hope you are recovering well after your [Treatment]. Please reply here if you have any pain or questions.'
    }
]

export const DEFAULT_SETTINGS: ClinicSettings = {
    clinicName: 'Dr. Sayuja Clinic',
    logo: null,
    doctorName: 'Dr. Sayuja',
    qualification: 'BDS, MDS',
    address: '2nd Floor, Lakeview Plaza, Panampilly Nagar, Kochi, Kerala 682036',
    phone: '+91 90000 00100',
    defaultAdvice: 'Avoid hot, hard and sticky food for 24 hours.\nBrush twice daily and rinse your mouth after every meal.\nComplete the full course of medicines as prescribed.\nContact the clinic if pain, swelling or bleeding increases.',
    footer: 'Clinic hours: Monday to Saturday, 9:30 AM to 7:30 PM. For dental emergencies call +91 90000 00100.',
    signatureName: 'Dr. Sayuja',
    signatureDetails: 'BDS, MDS · Dental Surgeon',
    printHeader: 'full',
    letterheadSpace: 45,
    showLogo: true,
    showComplaint: true,
    printTextSize: 'standard',
    complaints: DEFAULT_COMPLAINTS,
    treatments: DEFAULT_TREATMENTS,
    medicineInstructions: DEFAULT_MEDICINE_INSTRUCTIONS,
    countryCode: '91',
    templates: DEFAULT_TEMPLATES,
    defaultTemplateId: 'tpl-follow-up'
}

export const PATIENTS_PAGE_SIZE = 25

export const LOGO_MAX_SIZE = 320
